import { ArrowRight, Instagram } from "lucide-react";
import { IMAGES, WHATSAPP_URL, PSYCHOLOGIST, INSTAGRAM_HANDLE, INSTAGRAM_URL } from "../../lib/site";

export const Hero = () => {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 overflow-hidden"
        >
            {/* Soft decorative shape */}
            <div
                aria-hidden
                className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-40"
                style={{ background: "radial-gradient(circle at 30% 30%, #D8B7B1 0%, transparent 70%)" }}
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                {/* Left: copy */}
                <div className="lg:col-span-7 reveal">
                    <p className="overline mb-6" data-testid="hero-overline">
                        Psicoterapia · {PSYCHOLOGIST.crp}
                    </p>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-[2.75rem] sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight text-[#2F2F2F]"
                    >
                        Existe uma vida emocional
                        <span className="block italic text-[#9f1e1e]"> mais leve esperando por você.</span>
                    </h1>

                    <p
                        data-testid="hero-subheadline"
                        className="mt-8 max-w-xl text-lg lg:text-xl leading-relaxed text-[#2F2F2F]/80"
                    >
                        Há momentos em que carregar tudo sozinho começa a pesar — nos relacionamentos, no corpo,
                        no silêncio. A psicoterapia é o espaço onde você reencontra clareza, vínculo e a sua
                        própria voz. Acompanho adultos e casais há {PSYCHOLOGIST.years} anos, com escuta atenta e método
                        baseado em evidências.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-whatsapp"
                            className="btn-primary"
                        >
                            Agendar minha sessão
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </a>
                        <a href="#sobre" data-testid="hero-cta-secondary" className="btn-ghost">
                            Conhecer Luara
                        </a>
                    </div>

                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-instagram"
                        className="mt-8 inline-flex items-center gap-2 text-sm text-[#2F2F2F]/70 hover:text-[#9f1e1e] transition-colors group"
                    >
                        <Instagram className="w-4 h-4" strokeWidth={1.5} />
                        <span className="border-b border-[#B7A6B0] group-hover:border-[#9f1e1e] pb-0.5 transition-colors">
                            Acompanhe no Instagram · {INSTAGRAM_HANDLE}
                        </span>
                    </a>

                    <div className="mt-12 flex items-center gap-8 flex-wrap">
                        <Stat number={PSYCHOLOGIST.years} label="anos de clínica" />
                        <div className="w-px h-10 bg-[#B7A6B0]/50" />
                        <Stat number="ACT" label="FAP · DBT" />
                        <div className="w-px h-10 bg-[#B7A6B0]/50" />
                        <Stat number="Online" label="& Presencial" />
                    </div>
                </div>

                {/* Right: editorial portrait */}
                <div className="lg:col-span-5 relative reveal reveal-delay-1">
                    <div
                        aria-hidden
                        className="absolute -top-6 -left-6 w-28 h-28 border border-[#9f1e1e]/40 hidden lg:block"
                    />
                    <div className="editorial-frame relative aspect-[4/5] bg-[#D8B7B1]/40">
                        <img
                            src={IMAGES.hero}
                            alt="Luara Ainá do Nascimento, psicóloga"
                            className="w-full h-full object-cover"
                            data-testid="hero-portrait"
                        />
                    </div>
                    <div
                        aria-hidden
                        className="absolute -bottom-6 -right-6 w-36 h-36 bg-[#B7A6B0]/30 hidden lg:block"
                    />
                    <div className="hidden lg:flex absolute -left-12 top-1/2 -translate-y-1/2 -rotate-90 origin-left">
                        <span className="overline text-[10px] whitespace-nowrap">
                            Psicóloga · Brasília · Online
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Stat = ({ number, label }) => (
    <div>
        <p className="font-display text-3xl text-[#9f1e1e] leading-none">{number}</p>
        <p className="text-xs uppercase tracking-[0.18em] text-[#2F2F2F]/60 mt-2">{label}</p>
    </div>
);
